import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import "./index.css";
import Dashboard from "./components/dashboard/Dashboard";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";

import RequireAuth from "./context/RequireAuth";
import PersistLogin from "./context/PersistLogin";
import Layout from "./Layout";
import LoginForm from "./components/login/LoginForm";

import Accommodation from "./components/accommodation/Accommodation";
import Blog from "./components/blog/Blog";
import SuggestionsMain from "./components/suggestions/SuggestionsMain";
import Contact from "./components/contact/Contact";
import EnLegalConditions from "./components/legal/Legal";
import Home from "./components/home/Home";
import PropertiesLanding from "./components/properties/PropertiesLanding";

import useAuth from "./hooks/useAuth";
import axios from "./api/axios";

import { defaultLanguage } from "./constants";
import BlogpostLayout from "./components/blog/BlogPostLayout";
import { CreateBlog } from "./components/blog/CreateBlog";
import EditBlogPostLayout from "./components/blog/EditBlogPostLayout";
import { SpecialOffers } from "./components/special-offers/SpecialOffers";
import StructuredData from "./StructuredData";
import { useTranslation } from "react-i18next";

const WEBSITE_URL = "/styles";
const WEBSITE_URL_ACC = "/dashboard";
const WEBSITE_URL_CONTENT = "/website";

function lightenHexColor(hexColor, percent) {
  // Convert hex color code to RGB values
  const red = parseInt(hexColor.slice(1, 3), 16);
  const green = parseInt(hexColor.slice(3, 5), 16);
  const blue = parseInt(hexColor.slice(5, 7), 16);

  // Calculate the new RGB values by increasing each component by the specified percentage
  const newRed = Math.round(red + ((255 - red) * percent) / 100);
  const newGreen = Math.round(green + ((255 - green) * percent) / 100);
  const newBlue = Math.round(blue + ((255 - blue) * percent) / 100);

  // Convert the new RGB values back to a hex color code
  const newHexColor =
    "#" +
    ((1 << 24) + (newRed << 16) + (newGreen << 8) + newBlue)
      .toString(16)
      .slice(1);

  return newHexColor;
}

const App = () => {
  const [color1, setColor1] = useState("#F8EDE3");
  const [color2, setColor2] = useState("#594545");
  const [color3, setColor3] = useState("#C58940");
  const [color4, setColor4] = useState("#289bc1");
  const [color5, setColor5] = useState("#000000");
  const [color6, setColor6] = useState("#594545");
  const [lighterColor3, setLighterColor3] = useState("");
  const [lighterColor4, setLighterColor4] = useState("");
  const [headingsStep1, setHeadingsStep1] = useState("");
  const [headingsStep2, setHeadingsStep2] = useState("");
  const [headingsStep3, setHeadingsStep3] = useState("");
  const [headingsStep4, setHeadingsStep4] = useState("");
  const [paragraphColor, setParagraphColor] = useState("");
  const [fontHeading, setFontHeading] = useState("");
  const [fontText, setFontText] = useState("");
  const [showSpecialOffers, setShowSpecialOffers] = useState(true);
  const [accommodationName, setAccommodationName] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  const { username } = useAuth();
  const { i18n } = useTranslation();

  const [stylesLoaded, setStylesLoaded] = useState(false);
  const art = defaultLanguage;
  const title = "accomodationForm";

  useEffect(() => {
    const getContent = async () => {
      await axios
        .get(`${WEBSITE_URL_ACC}`, {
          params: { username, art, title },
        })
        .then((res) => {
          if (res.data.entry) {
            setAccommodationName(res.data.entry.nameAccommodation || "");
            setShowSpecialOffers(res?.data?.entry?.showSpecialOffers ?? true);
          }
        })
        .catch((err) => {
          if (err?.response) {
            console.error("HTTP error! Status:", err.response.status);
            console.error("Response:", err.response);
          } else if (err.request) {
            console.error("No response received:", err.request);
          } else {
            console.error("Error setting up the request:", err.message);
          }
        });
    };
    getContent();
  }, [username, art, title]);

  useEffect(() => {
    const getMetaContent = async () => {
      try {
        const [titleResponse, descriptionResponse] = await Promise.all([
          axios.get(`${WEBSITE_URL_CONTENT}`, {
            params: { username, art: i18n.language, title: "metaTitle" },
          }),
          axios.get(`${WEBSITE_URL_CONTENT}`, {
            params: { username, art: i18n.language, title: "metaDescription" },
          }),
        ]);

        const fetchedTitle = titleResponse?.data?.entry?.content;
        const fetchedDescription = descriptionResponse?.data?.entry?.content;
        setMetaTitle((fetchedTitle && fetchedTitle !== 'default text') ? fetchedTitle : null);
        setMetaDescription((fetchedDescription && fetchedDescription !== 'default text') ? fetchedDescription : null);
      } catch (err) {
        if (err?.response) {
          console.error("HTTP error! Status:", err.response.status);
          console.error("Response:", err.response);
        } else if (err.request) {
          console.error("No response received:", err.request);
        } else {
          console.error("Error setting up the request:", err.message);
        }
        setMetaTitle(null);
        setMetaDescription(null);
      }
    };

    getMetaContent();
  }, [username, i18n.language]);

  useEffect(() => {
    const upsertMetaDescription = (content) => {
      const current = (content || "").trim();
      let descriptionTag = document.querySelector('meta[name="description"]');

      if (!current) {
        if (descriptionTag) descriptionTag.remove();
        return;
      }

      if (!descriptionTag) {
        descriptionTag = document.createElement("meta");
        descriptionTag.setAttribute("name", "description");
        document.head.appendChild(descriptionTag);
      }
      descriptionTag.setAttribute("content", current);
    };

    upsertMetaDescription(metaDescription);
    document.title = (metaTitle && (metaTitle || "").trim()) || "Welcome";
  }, [metaDescription, metaTitle]);

  useEffect(() => {
    const getContent = async () => {
      await axios
        .get(`${WEBSITE_URL}`, {
          params: { username },
        })
        .then((res) => {
          setColor1(res?.data?.entry?.color1);
          setColor2(res?.data?.entry?.color2);
          setColor3(res?.data?.entry?.color3);
          setColor4(res?.data?.entry?.color4);
          setColor5(res?.data?.entry?.color5);
          setColor6(res?.data?.entry?.color6);
          setFontHeading(res?.data?.entry?.fontHeading);
          setFontText(res?.data?.entry?.fontText);
          setParagraphColor(lightenHexColor(res?.data?.entry?.color2, 20));
          setLighterColor3(lightenHexColor(res?.data?.entry?.color3, 30));
          setLighterColor4(lightenHexColor(res?.data?.entry?.color4, 80));
          const baseHeadingColor = res?.data?.entry?.color2;
          const step1 = lightenHexColor(baseHeadingColor, 40);
          const step2 = lightenHexColor(step1, 40);
          const step3 = lightenHexColor(step2, 40);
          const step4 = lightenHexColor(step3, 40);
          setHeadingsStep1(step1);
          setHeadingsStep2(step2);
          setHeadingsStep3(step3);
          setHeadingsStep4(step4);
          setStylesLoaded(true);
        })
        .catch((err) => {
          if (err?.response) {
            console.error("HTTP error! Status:", err.response.status);
            console.error("Response:", err.response);
          } else if (err.request) {
            console.error("No response received:", err.request);
          } else {
            console.error("Error setting up the request:", err.message);
          }
        });
    };
    getContent();
  }, [username]);

  const style = {
    "--color-bg": color1,
    "--color-nv": color2,
    "--color-nvbar": color6,
    "--color-nv-text": color5,
    "--color-headings": color2,
    "--color-headings-step-1": headingsStep1,
    "--color-headings-step-2": headingsStep2,
    "--color-headings-step-3": headingsStep3,
    "--color-headings-step-4": headingsStep4,
    "--color-stripe": color3,
    "--color-stripe-light": lighterColor3,
    "--color-button": color4,
    "--color-button-light": lighterColor4,
    "--color-text_paragraph": paragraphColor,
    "--font-fm-ser": fontHeading,
    "--font-fm-ss": fontText,
  };

  return (
    <div style={style}>
      <StructuredData/>
      {stylesLoaded ? (
        <Routes>
          <Route path="/" element={<Layout primaryColor={color4} siteName={accommodationName} />}>
            {/* public routes */}

            <Route key="home" path="/home" element={<Home />} />
            <Route
              key="practicalities"
              path="/practicalities"
              element={<Accommodation />}
            />
            <Route key="blog" path="/blog" element={<Blog />} />
            <Route
              key="prices"
              path="/prices"
              element={<SpecialOffers showSpecialOffers={showSpecialOffers} />}
            />
            <Route
              key="blog"
              path="/blog/:post_id"
              element={<BlogpostLayout />}
            />
            <Route
              key="suggestions"
              path="/suggestions"
              element={<SuggestionsMain />}
            />
            <Route key="contact" path="/contact" element={<Contact />} />
            <Route key="terms" path="/terms" element={<EnLegalConditions />} />
            <Route key="login" path="/login" element={<LoginForm />} />

            {/* Redirects */}
            {/* "/" root path redirects to homepage */}
            <Route path="/" element={<Navigate to="/home" />} />
            <Route
              path="/suggesties"
              element={<Navigate to="/suggestions" />}
            />
            <Route
              path="/accomodatie"
              element={<Navigate to="/practicalities" />}
            />
            <Route
              path="/special-offers"
              element={<Navigate to="/prices" replace />}
            />

            {/* protected routes*/}
            <Route element={<PersistLogin />}>
              <Route element={<RequireAuth allowedRole={["User","Admin"]}/>}>
                <Route key="edit-dash" path="/edit/dashboard" element={<Dashboard />} />
                <Route key="edit-properties" path="/edit/properties" element={<PropertiesLanding />} />
                <Route key="edit-home" path="/edit/home" element={<Home />} />
                <Route key="edit-blog" path="/edit/blog" element={<Blog />} />
                <Route
                  key="edit-blog"
                  path="/edit/blog/add"
                  element={<CreateBlog />}
                />
                <Route
                  key="edit-practicalities"
                  path="/edit/practicalities"
                  element={<Accommodation />}
                />
                <Route
                  key="edit-prices"
                  path="/edit/prices"
                  element={<SpecialOffers showSpecialOffers={showSpecialOffers} />}
                />
                <Route
                  key="edit-special-offers-redirect"
                  path="/edit/special-offers"
                  element={<Navigate to="/edit/prices" replace />}
                />
                <Route
                  key="edit-suggestions"
                  path="/edit/suggestions"
                  element={<SuggestionsMain />}
                />
                <Route
                  key="blogPosts_english"
                  path="/edit/blog/:post_id"
                  element={<EditBlogPostLayout />}
                />
                <Route
                  key="edit-contact"
                  path="/edit/contact"
                  element={<Contact />}
                />
                <Route
                  key="edit-terms"
                  path="/edit/terms"
                  element={<EnLegalConditions />}
                />
              </Route>
            </Route>
          </Route>
        </Routes>
      ) : (
        <div className="loader-wrapper">Loading...</div>
      )}
    </div>
  );
};

export default App;
